import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Button,
  Field,
  FileUpload,
  IconButton,
  MetaDefault,
  toasterApi,
} from '@terra/ui-design-system';
import {
  Cancel01Icon,
  CheckmarkCircle02Icon,
  Upload01Icon,
} from '@terra/ui-icons';
import { MAX_CERTIFICATE_FILE_BYTES } from './client-tls-form.model';

type CertificateFileFieldProps = {
  label: string;
  accept: string;
  fileName?: string;
  onFiles: (files: File[]) => void | Promise<void>;
  onClear: () => void;
};

export function CertificateFileField({
  label,
  accept,
  fileName,
  onFiles,
  onClear,
}: CertificateFileFieldProps) {
  const t = useTranslations('settings.clientTls');

  return (
    <Field.Root>
      <Field.Label fontSize="sm">{label}</Field.Label>
      <FileUpload.Root
        maxFiles={1}
        accept={accept}
        maxFileSize={MAX_CERTIFICATE_FILE_BYTES}
        onFileAccept={(details) => {
          void onFiles(details.files);
        }}
        onFileReject={(details) => {
          if (details.files[0]?.errors?.length) {
            toasterApi.error({
              title: t('toasts.invalidFileTitle'),
              description: details.files[0].errors.join(', '),
            });
          }
        }}
      >
        {fileName ? (
          <HStack
            gap="2"
            justify="space-between"
            w="full"
            p="2"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            bg="bg.subtle"
          >
            <HStack gap="2" minW="0">
              <CheckmarkCircle02Icon size={14} />
              <MetaDefault truncate>{fileName}</MetaDefault>
            </HStack>
            <HStack gap="1">
              <FileUpload.Trigger asChild>
                <Button size="xs" variant="ghost">
                  {t('fileField.replace')}
                </Button>
              </FileUpload.Trigger>
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={t('fileField.clearAriaLabel', { label })}
                onClick={onClear}
              >
                <Cancel01Icon size={14} />
              </IconButton>
            </HStack>
          </HStack>
        ) : (
          <VStack
            gap="2"
            justify="center"
            alignItems="center"
            w="full"
            py="3"
            px="2"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            borderStyle="dashed"
          >
            <MetaDefault color="fg.muted">{t('fileField.noFile')}</MetaDefault>
            <FileUpload.Trigger asChild>
              <Button size="xs" variant="outline">
                <Upload01Icon size={12} />
                {t('fileField.choose')}
              </Button>
            </FileUpload.Trigger>
          </VStack>
        )}
        <FileUpload.HiddenInput />
      </FileUpload.Root>
    </Field.Root>
  );
}
